import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import {
  ChatChoiceState,
  ChatMultipleChoiceState,
} from "@redotech/redo-return-ui/flows/chat-flow/chat-multiple-choice";
import { containsImg } from "@redotech/redo-return-ui/flows/chat-flow/step-rich-text-util";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  IconButton,
} from "@redotech/redo-web/button";
import { Divider } from "@redotech/redo-web/divider";
import LeftArrowIcon from "@redotech/redo-web/icon-old/left-arrow.svg";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { InputTheme, TextInput } from "@redotech/redo-web/text-input";
import { produce } from "immer";
import Quill from "quill";
import { memo, useRef } from "react";
import * as multipleChoiceCss from "../return-flow/multiple-choice.module.css";
import { RequiredMessage } from "../return-flow/required-message";
import { StepRichTextEditor } from "./step-rich-text";

export const ChatMultipleChoiceDetails = memo(
  function ChatMultipleChoiceDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<ChatMultipleChoiceState>) {
    // TODO: Not sure what the right permission mix is
    const canEditSupportSettings = useHasPermission(
      SettingsPermissions.SET_SUPPORT_SETTINGS,
    );
    const canEditReturnsSettings = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );
    const canEditSettings = canEditSupportSettings || canEditReturnsSettings;

    const messageQuillRef = useRef<Quill | undefined>(undefined);

    const handleAdd = useHandler(() =>
      setState((state) => ({
        ...state,
        choices: [...state.choices, { name: "", next: null, nextName: null }],
      })),
    );

    const handleMessageChange = useHandler(() => {
      if (!messageQuillRef.current) {
        return;
      }
      let message = messageQuillRef.current.getText();
      let messageHtml: string | undefined =
        messageQuillRef.current.getSemanticHTML();
      if (message === "\n") {
        message = "";
      }
      if (messageHtml === "<p></p>") {
        messageHtml = undefined;
      }

      if (messageHtml !== state.messageHtml || message !== state.message) {
        setState((state) => ({ ...state, message, messageHtml }));
      }
    });

    const handleFocus = useHandler((focused: boolean) => {
      if (!focused) {
        setState((state) => ({ ...state, attempted: true }));
      }
    });

    return (
      <>
        <LabeledInput
          errors={
            containsImg(state.messageHtml) ? ["Cannot contain images"] : []
          }
          label="Question"
        >
          <StepRichTextEditor
            htmlValue={state.messageHtml}
            onTextChange={handleMessageChange}
            quillRef={messageQuillRef}
            readOnly={!canEditSettings}
            textValue={state.message}
          />
        </LabeledInput>
        <IterableMap items={state.choices} keyFn={(_, index) => index}>
          {(choice, index) => (
            <StateChoiceComponent
              canEditSettings={canEditSettings}
              choice={choice}
              handleFocus={handleFocus}
              index={index}
              setState={setState}
              state={state}
              stepSelect={stepSelect}
            />
          )}
        </IterableMap>
        <Divider />
        <div>
          <Button
            disabled={!canEditSettings}
            onClick={handleAdd}
            size={ButtonSize.SMALL}
            theme={ButtonTheme.OUTLINED}
          >
            + Add option
          </Button>
        </div>
        {!state.choices.length && (
          <RequiredMessage>Must have at least one option</RequiredMessage>
        )}
      </>
    );
  },
);

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditSettings,
  choice,
  index,
}: {
  state: ChatMultipleChoiceState;
  setState: StepTypeDetailsProps<ChatMultipleChoiceState>["setState"];
  stepSelect: StepTypeDetailsProps<ChatMultipleChoiceState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditSettings: boolean;
  choice: ChatChoiceState;
  index: number;
}) {
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].name = name;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].nextName = null;
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined | "base") => {
    if (next === "base") {
      setState((state) =>
        produce(state, (state) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].next = null;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].nextName = "base";
        }),
      );
    } else {
      setState((state) =>
        produce(state, (state) => {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].next = next || null;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.choices[index].nextName = null;
        }),
      );
    }
  });
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <>
      <Divider />
      <LabeledInput
        label={
          <div className={multipleChoiceCss.label}>
            <div>Option #{index + 1}</div>
            {canEditSettings && (
              <>
                <IconButton
                  disabled={!index}
                  onClick={handleUp}
                  size={ButtonSize.SMALL}
                >
                  <LeftArrowIcon className={multipleChoiceCss.upArrow} />
                </IconButton>
                <IconButton
                  disabled={index === state.choices.length - 1}
                  onClick={handleDown}
                  size={ButtonSize.SMALL}
                >
                  <LeftArrowIcon className={multipleChoiceCss.downArrow} />
                </IconButton>
                <IconButton onClick={handleDelete} size={ButtonSize.SMALL}>
                  <TrashIcon />
                </IconButton>
              </>
            )}
          </div>
        }
      >
        <TextInput
          error={state.attempted && !choice.name}
          onChange={setName}
          onFocus={(focused: boolean) => {
            if (!choice.name) {
              handleFocus(focused);
            }
          }}
          readonly={!canEditSettings}
          theme={InputTheme.FORM}
          value={choice.name}
        />
      </LabeledInput>
      <LabeledInput
        errors={
          state.attempted && (!choice.next || !choice.nextName)
            ? ["Required"]
            : []
        }
        label={`Next step #${index + 1}`}
      >
        {stepSelect({
          value: choice.nextName ? choice.nextName : choice.next,
          valueChange: setStep,
          type: ModelStep.CHAT_MULTIPLE_CHOICE,
        })}
      </LabeledInput>
    </>
  );
});
